import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import MarkdownView from '../../components/MarkdownView';
import { EmptyState, Loading } from '../../components/StatusStates';
import { usePost, usePosts } from '../../hooks/use-public';
import { formatDateChinese } from '../../lib/format';
import { useSeo } from '../../lib/seo';
import { assetUrl } from '../../api/client';
import '../../styles/public.css';

export default function PostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading, isError, error, refetch } = usePost(slug);

  useSeo({
    title: post?.title ?? '文章',
    description: post?.excerpt ?? '一篇来自铁头功的博客的文章。',
  });

  // 相关文章：同标签的已发布文章（取最多 100 条后在前端匹配）
  const relatedQuery = usePosts({ page: 1, pageSize: 100 });
  const related = post
    ? (relatedQuery.data?.items ?? [])
        .filter((item) => item.id !== post.id && item.tags.some((tag) => post.tags.includes(tag)))
        .slice(0, 2)
    : [];

  return (
    <div className="container article-shell">
      {isLoading ? (
        <Loading text="文章加载中…" />
      ) : isError || !post ? (
        <EmptyState
          title="文章不存在或尚未发布"
          description={isError ? error?.message : '该内容可能仍为草稿，或链接已变更。'}
          action={
            isError ? (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => refetch()}>
                重新加载
              </button>
            ) : (
              <Link to="/blog" className="btn btn-primary btn-sm">
                返回博客列表
              </Link>
            )
          }
        />
      ) : (
        <article className="fade-up">
          <Link to="/blog" className="article-back">
            <ArrowLeft size={14} aria-hidden="true" />
            返回博客
          </Link>

          <header className="article-header">
            <div className="tag-row" style={{ marginBottom: '0.9rem' }}>
              {post.tags.map((tag) => (
                <span key={tag} className="tag tag-soft">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="article-title">{post.title}</h1>
            <div className="post-meta">
              <time dateTime={post.publishedAt ?? undefined}>
                {formatDateChinese(post.publishedAt)}
              </time>
              <span className="post-meta-dot" aria-hidden="true" />
              <span className="icon-inline">
                <Clock size={12} aria-hidden="true" />
                约 {post.readingTime} 分钟读完
              </span>
            </div>
          </header>

          {post.coverImage && (
            <img
              className="article-cover"
              src={assetUrl(post.coverImage) ?? undefined}
              alt={post.title}
              loading="lazy"
            />
          )}

          <MarkdownView content={post.content ?? ''} />

          {related.length > 0 && (
            <footer className="article-footer">
              <p className="aside-heading" style={{ marginBottom: '0.75rem' }}>
                相关文章
              </p>
              <div className="post-list" style={{ borderTop: 'none' }}>
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/blog/${item.slug}`}
                    className="post-card"
                    style={{ padding: '0.9rem 0' }}
                  >
                    <h3 className="post-card-title" style={{ fontSize: '1.05rem' }}>
                      {item.title}
                    </h3>
                    <div className="post-meta">{formatDateChinese(item.publishedAt)}</div>
                  </Link>
                ))}
              </div>
            </footer>
          )}
        </article>
      )}
    </div>
  );
}
